/**
 * 인증 상태 관리 Context
 * 로그인 상태, 사용자 정보, 로그인/로그아웃 함수를 전역으로 제공합니다.
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { login as apiLogin, logout as apiLogout, getCurrentUser, isAuthenticated, getToken } from '../services/admin/authService';
import type { AdminUser, LoginRequest } from '../services/admin/types';

// Context 타입 정의
interface AuthContextType {
  // 상태
  user: AdminUser | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  error: string | null;
  
  // 함수
  login: (credentials: LoginRequest) => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
}

// Context 생성
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider Props 타입
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider 컴포넌트
 * 앱 전체에 인증 상태를 제공합니다.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 로그인 상태 계산
  const isLoggedIn = !!user && isAuthenticated();

  /**
   * 초기 인증 상태 확인
   * 저장된 토큰이 있으면 사용자 정보를 조회합니다.
   */
  useEffect(() => {
    const initAuth = async () => {
      const token = getToken();
      
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await getCurrentUser();
        
        if (response.success && response.data?.user) {
          setUser(response.data.user);
        } else {
          // 토큰이 유효하지 않으면 초기화
          setUser(null);
        }
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * 로그인 함수
   * @param credentials 이메일과 비밀번호
   * @returns 로그인 성공 여부
   */
  const login = useCallback(async (credentials: LoginRequest): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiLogin(credentials);

      if (response.success && response.data) {
        setUser(response.data.user);
        return true;
      } else {
        setError(response.error?.message || '로그인에 실패했습니다.');
        return false;
      }
    } catch {
      setError('네트워크 오류가 발생했습니다.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * 로그아웃 함수
   */
  const logout = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    try {
      await apiLogout();
    } finally {
      setUser(null);
      setError(null);
      setIsLoading(false);
    }
  }, []);

  /**
   * 에러 초기화 함수
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Context 값
  const value: AuthContextType = {
    user,
    isLoggedIn,
    isLoading,
    error,
    login,
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuth 훅
 * AuthContext를 사용하기 위한 커스텀 훅입니다.
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}

export default AuthContext;
